import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, deleteResourceTemplate } from '../../../api/users-api';
import { handleErrors } from '../../../utils/errors';
import { ResourceTemplateModel } from '../../../../types';
import SharedDialog from '../../../shared/shared-dialog';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  CalendarRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { Messages } from '../../../utils/message-constants';
import Typography from '@mui/material/Typography';

interface DeleteResourceTemplateDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedTemplate: ResourceTemplateModel;
  changeLogRoles: string[];
}

export default function DeleteResourceTemplateDialog({
  open,
  handleClose,
  selectedTemplate,
  changeLogRoles,
}: DeleteResourceTemplateDialogProps) {
  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      deleteResourceTemplate(selectedTemplate, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Deleted('Resource template'),
      });

      queryClient.setQueryData<ResourceTemplateModel[] | undefined>(
        [UserKeys.CalendarResourceTemplates],
        (old) => old?.filter((template) => template.id !== selectedTemplate.id)
      );

      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Deleting(
          'this resource template'
        ),
      });
    },
    onSettled: () => {
      createEvent(
        analytics.calendars.resource_templates.delete_resource_template,
        {
          module: ModuleList.modules.calendars.name,
          section: ModuleList.modules.calendars.sections.resourceTemplates,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        CalendarRoles.DeleteResourceTemplate.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <>
      <SharedDialog
        open={open}
        handleClose={handleClose}
        title="Delete Resource Template"
        dialogContent={
          <Typography>
            {`Delete the resource template `}
            <Typography component="span" sx={{ fontWeight: 'bold' }}>
              {selectedTemplate.name}
            </Typography>
            {`?`}
          </Typography>
        }
        primaryButton={{
          title: 'Delete Template',
          action: handleSubmit,
          loading: isPending,
          disabled: isPending,
          color: 'error',
        }}
      />
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={mutate}
        mutateLoading={isPending}
      />
    </>
  );
}
